import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const CONSENT_KEY = 'salonite365_cookie_consent';
const CONSENT_VERSION = '1.0';
const SESSION_KEY = 'salonite365_consent_session';
const API_URL = import.meta.env.VITE_REACT_APP_API_URL || '';

// ─── Session identifier (anonymous, per browser) ────────────────────
// Random ID lets the DPO correlate multiple consent changes from the same
// browser without needing an authenticated user. Persisted in localStorage
// so it survives page reloads; never sent to third-party trackers.
function getSessionIdentifier() {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      sid = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch { return null; }
}

// ─── Google Consent Mode v2 ─────────────────────────────────────────
// The gtag.js script and consent-default ('denied') are set in index.html
// so GA's verification crawler can detect the tag in static HTML. This
// module only upgrades/downgrades consent buckets at runtime based on the
// user's cookie-banner choice.

function updateGtagConsent({ analytics, marketing }) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied',
  });
}

async function postConsentToServer({ analytics, marketing, action }) {
  if (!API_URL) return;
  try {
    await fetch(`${API_URL}/api/gdpr/cookie-consent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Cookie-consent endpoint is public; server best-effort resolves user_id
      // from Bearer if present, otherwise anchors by session_identifier.
      body: JSON.stringify({
        session_identifier: getSessionIdentifier(),
        analytics: !!analytics,
        marketing: !!marketing,
        action,
      }),
      credentials: 'omit',
      keepalive: true,
    });
  } catch { /* non-blocking: local storage is the source of truth */ }
}

export default function CookieConsent() {
  const { t } = useTranslation('legal');
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  const openBanner = useCallback(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setPreferences({
          necessary: true,
          analytics: !!parsed.analytics,
          marketing: !!parsed.marketing,
        });
      } catch { /* fall through with defaults */ }
    }
    setVisible(true);
  }, []);

  useEffect(() => {
    // Consent defaults ('denied') are already set in index.html before gtag.js
    // loads. On mount we only need to check if the user previously made a
    // choice and upgrade the consent buckets accordingly.
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
     let parsed;
    try {
      parsed = JSON.parse(stored);
    } catch {
      // Corrupted consent JSON — one-time synchronous fallback on mount,
      // no external event to hang this update on.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setVisible(true);
      return;
    }
    if (parsed.version !== CONSENT_VERSION) {
      // Policy version bumped — re-prompt and pre-fill prior choices.
      setPreferences({
        necessary: true,
        analytics: !!parsed.analytics,
        marketing: !!parsed.marketing,
      });
      setVisible(true);
      return;
    }
    // Prior valid consent — apply it silently so GA picks up the grant.
    updateGtagConsent({ analytics: !!parsed.analytics, marketing: !!parsed.marketing });
  }, []);

  // Footer / Privacy Policy page can re-open the banner via this event.
  useEffect(() => {
    const handler = () => openBanner();
    window.addEventListener('cookie-consent-open', handler);
    return () => window.removeEventListener('cookie-consent-open', handler);
  }, [openBanner]);

  const saveConsent = (accepted, overridePreferences, allAccepted = false, action) => {
    const prefs = overridePreferences || preferences;
    const finalPrefs = {
      necessary: true,
      analytics: accepted ? !!prefs.analytics : false,
      marketing: accepted ? !!prefs.marketing : false,
    };
    const consent = {
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      ...finalPrefs,
      all_accepted: allAccepted,
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setVisible(false);

    // 1. Update Google Consent Mode buckets so GA (already loaded via
    //    index.html) starts/stops collecting based on the user's choice.
    updateGtagConsent(finalPrefs);
    // 2. Persist server-side for the DPO audit trail (fire-and-forget).
    postConsentToServer({ ...finalPrefs, action });
    // 3. Preserve the existing DOM event for any downstream listeners.
    window.dispatchEvent(new CustomEvent('cookie-consent-updated', { detail: consent }));
  };

  const acceptAll = () => {
    const allAccepted = { necessary: true, analytics: true, marketing: true };
    setPreferences(allAccepted);
    saveConsent(true, allAccepted, true, 'accept_all');
  };

  const acceptSelected = () => saveConsent(true, preferences, false, 'accept_selected');

  const rejectOptional = () => {
    const rejected = { necessary: true, analytics: false, marketing: false };
    setPreferences(rejected);
    saveConsent(false, rejected, false, 'reject_optional');
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999]"
      style={{
        background: "rgba(255,255,255,0.82)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderTop: "1px solid rgba(255,255,255,0.90)",
        boxShadow: "0 -8px 40px rgba(100,100,160,0.10)",
      }}
    >
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="flex flex-col gap-4">

          {/* Main message */}
          <div className="flex-1">
            <h3
              className="text-sm font-black mb-1"
              style={{ letterSpacing: "-0.01em", color: "#2D2D4E" }}
            >
              {t('cookies_title')}
            </h3>
            <p style={{ fontSize: "12px", color: "#8A8AAA", lineHeight: "1.6" }}>
              {t('cookies_description')}{' '}
              <a href="/privacy" style={{ color: "#6C8EEF", textDecoration: "underline", fontWeight: 500 }}>
                {t('cookies_privacy_link')}
              </a>
            </p>
          </div>

          {/* Expandable details */}
          {showDetails && (
            <div
              className="rounded-2xl p-4 space-y-3"
              style={{
                background: "rgba(108,142,239,0.06)",
                border: "1px solid rgba(108,142,239,0.15)",
              }}
            >
              {[
                { key: 'necessary', label: t('cookies_necessary'), desc: t('cookies_necessary_desc'), locked: true, checked: true },
                { key: 'analytics', label: t('cookies_analytics'), desc: t('cookies_analytics_desc'), locked: false, checked: preferences.analytics },
                { key: 'marketing', label: t('cookies_marketing'), desc: t('cookies_marketing_desc'), locked: false, checked: preferences.marketing },
              ].map(({ key, label, desc, locked, checked }) => (
                <label
                  key={key}
                  className={`flex items-center gap-3 ${locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={locked}
                    onChange={locked ? undefined : (e) => setPreferences(p => ({ ...p, [key]: e.target.checked }))}
                    className="w-4 h-4 rounded"
                    style={{ accentColor: "#6C8EEF" }}
                  />
                  <div>
                    <span style={{ fontSize: "13px", fontWeight: 600, color: "#2D2D4E" }}>{label}</span>
                    <p style={{ fontSize: "11px", color: "#8A8AAA", marginTop: "2px" }}>{desc}</p>
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-xs font-medium underline py-2 order-last sm:order-first transition hover:opacity-70"
              style={{ color: "#6C8EEF" }}
            >
              {showDetails ? t('cookies_hide_settings') : t('cookies_show_settings')}
            </button>

            <div className="flex gap-2 sm:ml-auto">
              <button
                onClick={rejectOptional}
                className="flex-1 sm:flex-none px-4 py-2.5 text-xs font-semibold rounded-xl transition-colors"
                style={{
                  color: "#5A5A7A",
                  background: "rgba(232,236,247,0.80)",
                  border: "1px solid rgba(108,142,239,0.15)",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(232,236,247,1)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(232,236,247,0.80)"}
              >
                {t('cookies_only_necessary')}
              </button>

              {showDetails ? (
                <button
                  onClick={acceptSelected}
                  className="flex-1 sm:flex-none px-5 py-2.5 text-xs font-bold text-white rounded-xl transition-all duration-200"
                  style={{
                    background: "linear-gradient(135deg, #8B7FC7, #6C8EEF)",
                    boxShadow: "0 4px 16px rgba(108,142,239,0.32)",
                  }}
                >
                  {t('cookies_save_choice')}
                </button>
              ) : (
                <button
                  onClick={acceptAll}
                  className="flex-1 sm:flex-none px-5 py-2.5 text-xs font-bold text-white rounded-xl transition-all duration-200"
                  style={{
                    background: "linear-gradient(135deg, #8B7FC7, #6C8EEF)",
                    boxShadow: "0 4px 16px rgba(108,142,239,0.32)",
                  }}
                >
                  {t('cookies_accept_all')}
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}